/**
 * Build step 6 — health score, client brief, engagement grid, at-risk list, targets, thresholds and the 12
 * flags (ADR-001 §11.8, §8.1, §8.2, §8.7, §8.11; operator decision 11).
 *
 * Visibility follows the client: managers (founder, ops_ad) all clients; an ops_member the clients they are
 * the current SPOC of; requesters and HR none. Out-of-scope ids are 404, never 403. Revenue appears only in
 * the brief, for managers (byAudience). Every write is audited (real actor + acting-as); viewing-as is
 * read-only. Two additions beyond the §11.8 table, both reads: `GET /briefs` (Team → Briefs) and
 * `GET /targets` (Clients → Targets across clients) — the screens list across clients.
 */
import { z } from 'zod';
import { IdempotencyHeaders, IfMatchHeaders } from './common.js';
import { BriefCardList, ClientBrief, ClientBriefManager, ClientBriefMember, ListBriefsQuery } from './brief.js';
import { ActivityKeyParams, AddEngagementTasksBody, AddEngagementTasksResponse, BookEngagementBody, ClientEngagement, EngagementActivity, EngagementActivityList, EngagementCell, EngagementCellParams, EngagementGrid, EngagementGridQuery, EngagementLog, LogEngagementBody, UpdateEngagementActivityBody, } from './engagement.js';
import { AtRiskQuery, ClientHealthDetail, ClientHealthDetailMember, HealthHistory, HealthHistoryQuery, HealthQuery, RiskItemPage } from './health.js';
import { FlagsResponse, PatchFlagsBody, PatchThresholdsBody, ThresholdsResponse } from './settings.js';
import { ClientTarget, ClientTargetList, CreateTargetBody, ImportTargetsBody, ImportTargetsResponse, ListTargetsQuery, TargetIdParams, UpdateTargetBody, } from './targets.js';
import { API_BASE, defineRoute } from './route.js';
const ClientIdOnly = z.object({ id: z.uuid() });
const NON_REQUESTER = ['clients.read.all', 'clients.read.own'];
const MANAGER_SETTINGS = ['settings.write'];
const SCOPE = 'Managers; or the client’s current SPOC. Others: 404.';
const WRITE_SCOPE = 'Managers, or the client’s current SPOC. Others: 404. Not while viewing as someone (403).';
const ETAG = { ETag: 'W/"<version>" of the returned resource' };
const CONFIG_ETAG = { ETag: 'W/"<config_version>"' };
export const step6Routes = {
    // ============================================================ health
    'health.get': defineRoute({
        id: 'health.get',
        method: 'GET',
        path: `${API_BASE}/clients/{id}/health`,
        summary: 'The client’s health score with its reason lines, inputs and the thresholds version',
        description: 'Latest snapshot, or the snapshot for ?as_of (404 when none was taken that day). A client never scored yet is ' +
            'scored on read and returned unsaved. Without toneReason.read the tone reasons and tone inputs are left out; ' +
            'the score still includes their points. The `other` audience (no toneReason.read — the same roles as no ' +
            'revenue.read) is ClientHealthDetailMember, which refuses a tone reason or a tone input.',
        access: { auth: 'session', anyOf: NON_REQUESTER, note: SCOPE },
        params: ClientIdOnly,
        query: HealthQuery,
        success: { status: 200, description: 'The score', schema: ClientHealthDetail, byAudience: { revenue: ClientHealthDetail, other: ClientHealthDetailMember } },
        errors: ['no_session', 'not_found', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'health.history': defineRoute({
        id: 'health.history',
        method: 'GET',
        path: `${API_BASE}/clients/{id}/health/history`,
        summary: 'The score over time, from the daily snapshots',
        description: '`to` defaults to today (IST), `from` to 90 days before `to`. The range is checked against those defaults: ' +
            '`?from=` alone runs to today, and must be on or before it and at most 366 days back (422). One point per day a snapshot exists, oldest first.',
        access: { auth: 'session', anyOf: NON_REQUESTER, note: SCOPE },
        params: ClientIdOnly,
        query: HealthHistoryQuery,
        success: { status: 200, description: 'Score history', schema: HealthHistory },
        errors: ['no_session', 'not_found', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    // ============================================================ brief
    'brief.get': defineRoute({
        id: 'brief.get',
        method: 'GET',
        path: `${API_BASE}/clients/{id}/brief`,
        summary: 'Everything the client brief shows, in one payload',
        description: '`revenue` is present for managers only (absent, not null, for members).',
        access: { auth: 'session', anyOf: NON_REQUESTER, note: SCOPE },
        params: ClientIdOnly,
        success: {
            status: 200,
            description: 'The brief',
            schema: ClientBrief,
            byAudience: { revenue: ClientBriefManager, other: ClientBriefMember },
        },
        errors: ['no_session', 'not_found'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'briefs.list': defineRoute({
        id: 'briefs.list',
        method: 'GET',
        path: `${API_BASE}/briefs`,
        summary: 'Team → Briefs: one card per client (line, product chips, engagement, next idea)',
        access: { auth: 'session', anyOf: ['team.read'], note: 'Managers. Others: 403.' },
        query: ListBriefsQuery,
        success: { status: 200, description: 'Brief cards, worst health first', schema: BriefCardList },
        errors: ['no_session', 'forbidden', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    // ============================================================ engagement
    'engagement.client': defineRoute({
        id: 'engagement.client',
        method: 'GET',
        path: `${API_BASE}/clients/{id}/engagement`,
        summary: 'The eight engagement cells for one client',
        access: { auth: 'session', anyOf: NON_REQUESTER, note: SCOPE },
        params: ClientIdOnly,
        success: { status: 200, description: 'The cells, in activity order', schema: ClientEngagement },
        errors: ['no_session', 'not_found'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'engagement.log': defineRoute({
        id: 'engagement.log',
        method: 'POST',
        path: `${API_BASE}/clients/{id}/engagement/{activity_key}/log`,
        summary: 'Record that an engagement activity was done',
        description: 'A note is required for mis and stake (422 on note). Closes any open engagement task for the cell; an MIS resets ' +
            'its clock. done_on may not be in the future or before the client started (422). An inactive activity is 422.',
        access: { auth: 'session', anyOf: NON_REQUESTER, note: WRITE_SCOPE },
        params: EngagementCellParams,
        headers: IdempotencyHeaders,
        body: LogEngagementBody,
        success: { status: 201, description: 'The log entry and the cell after it', schema: EngagementLog },
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    'engagement.book': defineRoute({
        id: 'engagement.book',
        method: 'POST',
        path: `${API_BASE}/clients/{id}/engagement/{activity_key}/book`,
        summary: 'Book a later date for an activity (or clear the booking with null)',
        description: 'booked_for must be today or later (422). Setting the same date twice is a no-op.',
        access: { auth: 'session', anyOf: NON_REQUESTER, note: WRITE_SCOPE },
        params: EngagementCellParams,
        body: BookEngagementBody,
        success: { status: 200, description: 'The updated cell', schema: EngagementCell },
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'write',
    }),
    'engagement.addTasks': defineRoute({
        id: 'engagement.addTasks',
        method: 'POST',
        path: `${API_BASE}/clients/{id}/engagement/tasks`,
        summary: '“Add what is due to the task list” — one task per due cell, for the SPOC',
        description: 'Idempotent per (client, activity) even without a key: a cell with an open engagement task is skipped ' +
            '(already_open), never duplicated. 201 even when everything was skipped.',
        access: { auth: 'session', anyOf: NON_REQUESTER, note: WRITE_SCOPE },
        params: ClientIdOnly,
        headers: IdempotencyHeaders,
        body: AddEngagementTasksBody,
        success: { status: 201, description: 'Tasks created and cells skipped', schema: AddEngagementTasksResponse },
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    'engagement.grid': defineRoute({
        id: 'engagement.grid',
        method: 'GET',
        path: `${API_BASE}/engagement/grid`,
        summary: 'The whole engagement grid, one row per client in scope',
        access: { auth: 'session', anyOf: NON_REQUESTER, note: 'Managers → all clients; ops_member → own clients. Others: 403.' },
        query: EngagementGridQuery,
        success: { status: 200, description: 'The grid, most overdue first', schema: EngagementGrid },
        errors: ['no_session', 'forbidden', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'engagementActivities.list': defineRoute({
        id: 'engagementActivities.list',
        method: 'GET',
        path: `${API_BASE}/engagement/activities`,
        summary: 'The eight engagement activities and their cadences',
        access: { auth: 'session', anyOf: NON_REQUESTER, note: 'Managers and ops members. Others: 403.' },
        success: { status: 200, description: 'Activities in display order', schema: EngagementActivityList },
        errors: ['no_session', 'forbidden'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'engagementActivities.update': defineRoute({
        id: 'engagementActivities.update',
        method: 'PATCH',
        path: `${API_BASE}/engagement/activities/{key}`,
        summary: 'Change an activity’s name, cadence, new-client cadence or switch it off (Settings)',
        description: 'A settings row: last write wins, audited, bumps config_version. Takes effect on the next read.',
        access: { auth: 'session', anyOf: MANAGER_SETTINGS, deny: 'forbidden', note: 'Founder and associate director. Others: 403.' },
        params: ActivityKeyParams,
        body: UpdateEngagementActivityBody,
        success: { status: 200, description: 'The activity', schema: EngagementActivity },
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'write',
    }),
    // ============================================================ at risk
    'atRisk.list': defineRoute({
        id: 'atRisk.list',
        method: 'GET',
        path: `${API_BASE}/at-risk`,
        summary: 'Team → At risk: waiting, late and going-quiet items, worst first',
        access: { auth: 'session', anyOf: ['team.read'], note: 'Managers. Others: 403.' },
        query: AtRiskQuery,
        success: { status: 200, description: 'Risk items, highest score first, with the tab summary', schema: RiskItemPage },
        errors: ['no_session', 'forbidden', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    // ============================================================ targets
    'targets.list': defineRoute({
        id: 'targets.list',
        method: 'GET',
        path: `${API_BASE}/targets`,
        summary: 'Clients → Targets: every target in scope for a quarter, with the tab stats',
        description: 'Default the current quarter. Sorted by owner, then balance % (most behind first).',
        access: { auth: 'session', anyOf: NON_REQUESTER, note: 'Managers → all; ops_member → own clients. Others: 403.' },
        query: ListTargetsQuery,
        success: { status: 200, description: 'Targets', schema: ClientTargetList },
        errors: ['no_session', 'forbidden', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'targets.listForClient': defineRoute({
        id: 'targets.listForClient',
        method: 'GET',
        path: `${API_BASE}/clients/{id}/targets`,
        summary: 'One client’s targets for a quarter',
        access: { auth: 'session', anyOf: NON_REQUESTER, note: SCOPE },
        params: ClientIdOnly,
        query: ListTargetsQuery,
        success: { status: 200, description: 'Targets', schema: ClientTargetList },
        errors: ['no_session', 'not_found', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'targets.create': defineRoute({
        id: 'targets.create',
        method: 'POST',
        path: `${API_BASE}/clients/{id}/targets`,
        summary: 'Add a target for the client',
        description: 'owner_id must be an active SPOC-eligible user (422 on owner_id). 409 when the client already has that quarter + metric + label.',
        // Open to members so the SPOC gets an honest 403 (a param route denied by anyOf answers 404); the handler finds the
        // client first (404 when the caller cannot see it), then refuses anyone without clients.edit.all (as step 5, 343e0f3).
        access: { auth: 'session', anyOf: NON_REQUESTER, note: 'Managers. The SPOC: 403; others 404.' },
        params: ClientIdOnly,
        headers: IdempotencyHeaders,
        body: CreateTargetBody,
        success: { status: 201, description: 'Created', schema: ClientTarget, headers: ETAG },
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed', 'conflict'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    'targets.update': defineRoute({
        id: 'targets.update',
        method: 'PATCH',
        path: `${API_BASE}/clients/{id}/targets/{tid}`,
        summary: 'Correct a target’s figures, note or owner',
        description: 'Managers (ADR §11.8: members read their own clients’ targets, they do not edit them). Sets source = manual.',
        // Open to members so the SPOC gets an honest 403 (a param route denied by anyOf answers 404); the handler finds the
        // client first (404 when the caller cannot see it), then refuses anyone without clients.edit.all (as step 5, 343e0f3).
        access: { auth: 'session', anyOf: NON_REQUESTER, note: 'Managers. The SPOC: 403; others 404.' },
        params: TargetIdParams,
        headers: IfMatchHeaders,
        body: UpdateTargetBody,
        success: { status: 200, description: 'Updated', schema: ClientTarget, headers: ETAG },
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed', 'precondition_failed'],
        idempotency: 'safe',
        concurrency: 'if-match',
        rateLimit: 'write',
    }),
    'targets.import': defineRoute({
        id: 'targets.import',
        method: 'POST',
        path: `${API_BASE}/targets/import`,
        summary: '“Paste from sheet”: update or add targets from pasted rows',
        description: 'Each row resolves its client by name and its owner to a user id, or is skipped with a reason — free-text names ' +
            'are never stored. A row matches by client + quarter + metric + label; without a label and with several targets ' +
            'for that client it is skipped as target_ambiguous. dry_run previews without writing. Rows are applied together or not at all.',
        access: { auth: 'session', anyOf: ['clients.edit.all'], note: 'Managers. Others: 403.' },
        headers: IdempotencyHeaders,
        body: ImportTargetsBody,
        success: { status: 200, description: 'Counts, skipped rows and the targets written', schema: ImportTargetsResponse },
        errors: ['no_session', 'forbidden', 'validation_failed'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    // ============================================================ settings
    'settings.thresholds': defineRoute({
        id: 'settings.thresholds',
        method: 'GET',
        path: `${API_BASE}/settings/thresholds`,
        summary: 'Every threshold and penalty, with the defaults and the current config_version',
        access: { auth: 'session', anyOf: MANAGER_SETTINGS, note: 'Founder and associate director. Others: 403.' },
        success: { status: 200, description: 'Thresholds', schema: ThresholdsResponse, headers: CONFIG_ETAG },
        errors: ['no_session', 'forbidden'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'settings.updateThresholds': defineRoute({
        id: 'settings.updateThresholds',
        method: 'PATCH',
        path: `${API_BASE}/settings/thresholds`,
        summary: 'Change any thresholds or penalties',
        description: 'If-Match is the config_version. The change is merged into the current set and checked as a whole (422 names ' +
            'the field when an ordering breaks, e.g. watch line above healthy line). Bumps config_version, is audited, and ' +
            'never rewrites a past health snapshot; scores recompute from now on.',
        access: { auth: 'session', anyOf: MANAGER_SETTINGS, note: 'Founder and associate director. Others: 403.' },
        headers: IfMatchHeaders,
        body: PatchThresholdsBody,
        success: { status: 200, description: 'The thresholds after the change', schema: ThresholdsResponse, headers: CONFIG_ETAG },
        errors: ['no_session', 'forbidden', 'validation_failed', 'precondition_failed'],
        idempotency: 'safe',
        concurrency: 'if-match',
        rateLimit: 'write',
    }),
    'settings.flags': defineRoute({
        id: 'settings.flags',
        method: 'GET',
        path: `${API_BASE}/settings/flags`,
        summary: 'The 12 flags: on or off, and how many are open',
        access: { auth: 'session', anyOf: MANAGER_SETTINGS, note: 'Founder and associate director. Others: 403.' },
        success: { status: 200, description: 'Flag states, in the guide’s order', schema: FlagsResponse, headers: CONFIG_ETAG },
        errors: ['no_session', 'forbidden'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'settings.updateFlags': defineRoute({
        id: 'settings.updateFlags',
        method: 'PATCH',
        path: `${API_BASE}/settings/flags`,
        summary: 'Switch flags on or off',
        description: 'If-Match is the config_version. Switching one off clears its open flag hits in the same transaction and stops ' +
            'evaluating it; switching it on starts evaluation at the next run of its tier. Bumps config_version, audited.',
        access: { auth: 'session', anyOf: MANAGER_SETTINGS, note: 'Founder and associate director. Others: 403.' },
        headers: IfMatchHeaders,
        body: PatchFlagsBody,
        success: { status: 200, description: 'Flag states after the change', schema: FlagsResponse, headers: CONFIG_ETAG },
        errors: ['no_session', 'forbidden', 'validation_failed', 'precondition_failed'],
        idempotency: 'safe',
        concurrency: 'if-match',
        rateLimit: 'write',
    }),
};
//# sourceMappingURL=routes.step6.js.map